import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { TDR } from 'tdr-common';
import { useOpenParametersUrlNormalization } from '../hooks/useOpenParametersUrlNormalization';
import { RestaurantGuardOutletContext } from './RestaurantGuard';

const GROUP_SIZE_KEY = 'groupSize';
const DATE_KEY = 'date';
const TIME_KEY = 'time';
export interface AvailabilityGuardContext extends RestaurantGuardOutletContext {
	searchFilters: SearchFilters;
	tables: TDR.Table[];
	updateSearchParams: (searchFilters: Partial<SearchFilters>, options?: { pathname?: string, hash?: string }) => void;
	previousTime: string;
	setPreviousTime: (arg1: string) => void;
	previousDate: string;
	setPreviousDate: (arg1: string) => void;
}

export interface SearchFilters {
	groupSize: number;
	date: string;
	time: string;
}

export function parseSearchParamsFilters(searchParams: URLSearchParams, timezone?: string): SearchFilters {
	let groupSize: number | undefined;
	let date: string | undefined;
	let time: string | undefined;

	if (searchParams.has(GROUP_SIZE_KEY)) {
		const tempGroupSize = parseInt(searchParams.get(GROUP_SIZE_KEY) || '');
		if (!isNaN(tempGroupSize)) {
			groupSize = tempGroupSize;
		}
	}

	if (searchParams.has(DATE_KEY)) {
		const tempDay = searchParams.get(DATE_KEY);
		if (tempDay) {
			const parsedDay = DateTime.fromISO(tempDay, { zone: timezone });
			if (parsedDay.isValid) {
				date = parsedDay.toISODate(); // Ensures YYYY-MM-DD format
			}
		}
	}

	if (searchParams.has(TIME_KEY)) {
		const tempTime = searchParams.get(TIME_KEY);
		if (tempTime && /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(tempTime)) {
			// Validates HH:MM format
			time = tempTime;
		}
	}

	return {
		groupSize,
		date,
		time
	};
}

export function serializeSearchFiltersForURL(searchFilters: Partial<SearchFilters>): URLSearchParams {
	const params = typeof window !== 'undefined'
		? new URLSearchParams(window.location.search)
		: new URLSearchParams();

	// Remove existing filter keys so we can re-set only the defined ones
	params.delete(GROUP_SIZE_KEY);
	params.delete(DATE_KEY);
	params.delete(TIME_KEY);

	if (searchFilters.groupSize !== undefined) {
		params.set(GROUP_SIZE_KEY, searchFilters.groupSize.toString());
	}

	if (searchFilters.date !== undefined) {
		params.set(DATE_KEY, searchFilters.date);
	}

	if (searchFilters.time !== undefined) {
		params.set(TIME_KEY, searchFilters.time);
	}

	return params;
}

const AVAILABILITY_PARAM_KEYS = ['groupSize', 'date', 'time'] as const;

export function getSearchStringWithoutAvailabilityParams(searchParams: URLSearchParams): string {
	const params = new URLSearchParams(searchParams);
	for (const key of AVAILABILITY_PARAM_KEYS) {
		params.delete(key);
	}
	return params.toString();
}

export const AvailabilityGuard = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [previousTime, setPreviousTime] = useState<string>('');
	const [previousDate, setPreviousDate] = useState<string>('');
	const location = useLocation();
	const navigate = useNavigate();
	//In mod flows this wont exists
	const parentContext = useOutletContext<RestaurantGuardOutletContext>();

	const searchFilters = parseSearchParamsFilters(searchParams, parentContext?.restaurant?.timezone);

	// If date is in the past, replace with today and update the URL so header and context stay in sync
	useEffect(() => {
		if (!searchFilters.date) {
			return;
		}
		const today = new Date().toISOString().split('T')[0];
		if (searchFilters.date < today) {
			setSearchParams(serializeSearchFiltersForURL({ ...searchFilters, date: today }), { replace: true });
		}
	}, [searchFilters.date]);

	useOpenParametersUrlNormalization({ searchFilters });

	// If group size not set, clear the date and time keys
	useEffect(() => {
		if (!searchParams.has(GROUP_SIZE_KEY)) {
			const hadDateOrTime = searchParams.has(DATE_KEY) || searchParams.has(TIME_KEY);
			if (!hadDateOrTime) {
				return; // nothing to clear
			}
			searchParams.delete(DATE_KEY);
			searchParams.delete(TIME_KEY);
			setSearchParams(searchParams);
		}
	}, [searchParams, setSearchParams]);

	// If time set without date, clear the time key
	useEffect(() => {
		if (searchParams.has(TIME_KEY) && !searchParams.has(DATE_KEY)) {
			searchParams.delete(TIME_KEY);
			setSearchParams(searchParams);
		}
	}, [searchParams, setSearchParams]);

	function setSearchFilters(newSearchFilters: Partial<SearchFilters>, options?: { pathname?: string, hash?: string }) {
		const serializedParams = serializeSearchFiltersForURL(newSearchFilters);
		navigate(
			{
				pathname: options?.pathname || location.pathname,
				search: serializedParams.toString(),
				hash: options?.hash || location.hash
			},
			{ replace: true }
		);
	}

	const updateSearchParams = (newFilters: Partial<SearchFilters>, options?: { pathname?: string, hash?: string }) =>
		setSearchFilters({ ...searchFilters, ...newFilters }, options);

	return (
		<>
			{
				/*
					TODO
					This <AnimatedOutlet /> will have to be rethought to achieve smooth page transitions.
					In the checkout flow, it causes the <CheckoutProvider /> to re-render and reset the checkoutState, breaking the flow 🙁
				*/
			}

			{/* <AnimatedOutlet context={{ searchFilters, updateSearchParams, ...parentContext } satisfies AvailabilityGuardContext} /> */}

			<Outlet context={{ searchFilters, updateSearchParams, previousTime, setPreviousTime, previousDate, setPreviousDate, ...parentContext } satisfies AvailabilityGuardContext} />
		</>
	);
};
